import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTheme } from 'styled-components';
import { Button } from '../form/button/button';
import { FloatingObject } from '../floating-object/floating-object';
import { PositionType } from '../../../utils/calculate-position';
import { useRootStore } from '../../../providers/root-store-provider';
import { useConfirmTrigger } from '../../../hooks/use-confirm-trigger';

interface ConfirmPropsInterface {
    children: React.ReactNode;
    isProcessing?: boolean;
    isLoading?: boolean;
    position?: PositionType;
    title?: React.ReactNode;
    content?: React.ReactNode;
    confirmTitle?: string;
    cancelTitle?: string;
    onConfirm: () => Promise<void>;
    variant?: 'positive' | 'negative';
}

export const Confirm: React.FC<ConfirmPropsInterface> = observer(
    ({
        children,
        isProcessing = false,
        position = 'bottom-left',
        title,
        content,
        confirmTitle,
        cancelTitle,
        onConfirm,
        variant = 'negative',
        isLoading,
    }: ConfirmPropsInterface) => {
        const theme = useTheme();
        const { isActive, trigger } = useConfirmTrigger(children);
        const rootStore = useRootStore();
        const { confirmStore } = rootStore;

        const handleCancel = () => {
            confirmStore.clearActiveConfirm();
        };

        const handleConfirm = async () => {
            await onConfirm();
            confirmStore.clearActiveConfirm();
        };

        return (
            <FloatingObject
                trigger={trigger}
                onClickOutside={handleCancel}
                content={
                    <div className="max-w-[24rem] rounded-medium bg-surface text-center normal-case shadow-[0_0.8rem_2.4rem_rgba(0,0,0,0.18)]">
                        {title && (
                            <div
                                className="mb-xsmall border-b border-solid border-border p-xsmall text-center font-bold"
                                style={{ color: theme.palette.color[variant].main }}
                            >
                                {title}
                            </div>
                        )}
                        <div className="flex justify-between gap-xsmall whitespace-nowrap px-xsmall pb-xsmall">
                            <Button size="vsmall" variation="transparent" onClick={handleCancel}>
                                {cancelTitle}
                            </Button>
                            <Button
                                loading={isProcessing}
                                variant={variant}
                                size="vsmall"
                                variation="filled"
                                onClick={handleConfirm}
                            >
                                {confirmTitle}
                            </Button>
                        </div>
                        {content && (
                            <div className="border-t border-solid border-border p-xsmall text-center text-vsmall font-normal leading-vsmall">
                                {content}
                            </div>
                        )}
                    </div>
                }
                position={position}
                isVisible={isActive && !isLoading}
            />
        );
    },
);
